import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, TextField, Button, Grid, Alert, Box,
  ToggleButton, ToggleButtonGroup, IconButton, LinearProgress,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import api from '../api';
import { priorityColors, navyBlue } from '../constants/colors';

export default function MaintenanceRequest() {
  const { assetNumber } = useParams();
  const [asset, setAsset] = useState(null);
  const [formData, setFormData] = useState({ priority: 'medium' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  useEffect(() => {
    loadAsset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetNumber]);

  const loadAsset = async () => {
    try {
      const response = await api.get(`/assets/number/${assetNumber}`);
      setAsset(response.data);
      setFormData({ ...formData, asset_id: response.data.id });
    } catch (err) {
      setError('Asset not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setUploadProgress(0);

    try {
      // Create work order first
      const response = await api.post('/work-orders', formData);
      const workOrderId = response.data.id;

      // Upload photo if selected
      if (selectedFile && workOrderId) {
        const photoData = new FormData();
        photoData.append('file', selectedFile);

        await api.post(`/uploads/work-order/${workOrderId}`, photoData, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
      }

      setSuccess(true);
      setError('');
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit maintenance request');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (loading) return <Container><Typography>Loading...</Typography></Container>;

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 5 }}>
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', textAlign: 'center' }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
            color: 'white',
            p: 3,
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Request Submitted Successfully!
            </Typography>
          </Box>
          <Box sx={{ p: 4 }}>
            <Typography variant="body1">
              Your maintenance request has been submitted and assigned to a technician.
              You will receive an email notification.
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 3,
                background: `linear-gradient(135deg, ${navyBlue} 0%, #2d5a8a 100%)`,
                borderRadius: 2,
                textTransform: 'none',
              }}
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 5 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${navyBlue} 0%, #2d5a8a 100%)`,
          color: 'white',
          p: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Maintenance Request
          </Typography>
        </Box>
        <Box sx={{ p: 4 }}>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {asset && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="h6">{asset.asset_number}</Typography>
            <Typography variant="body2" color="textSecondary">
              {asset.asset_description}
            </Typography>
            <Typography variant="body2">
              Location: {asset.location}
            </Typography>
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Title" required
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief description of the issue"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={4}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description of the problem"
              />
            </Grid>

            {/* Photo Upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: navyBlue, fontWeight: 600 }}>
                Add Photo (optional)
              </Typography>

              {!selectedFile ? (
                <Paper
                  component="label"
                  elevation={0}
                  sx={{
                    border: '2px dashed #9e9e9e',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'block',
                    bgcolor: 'transparent',
                    '&:hover': {
                      borderColor: navyBlue,
                      bgcolor: '#f8f9fa'
                    }
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <CameraIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Tap to take a photo or select image
                  </Typography>
                </Paper>
              ) : (
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {previewUrl ? (
                      <Box
                        component="img"
                        src={previewUrl}
                        alt="Preview"
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 2
                        }}
                      />
                    ) : (
                      <Box sx={{
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#e0e0e0',
                        borderRadius: 2
                      }}>
                        <ImageIcon sx={{ fontSize: 32, color: '#757575' }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={handleRemoveFile}
                      size="small"
                      sx={{ color: '#f44336' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: navyBlue, fontWeight: 600 }}>
                Priority *
              </Typography>
              <ToggleButtonGroup
                value={formData.priority}
                exclusive
                onChange={(e, value) => value && setFormData({ ...formData, priority: value })}
                fullWidth
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                {[
                  { value: 'low', label: 'Low', sublabel: 'Can wait' },
                  { value: 'medium', label: 'Medium', sublabel: 'Normal' },
                  { value: 'high', label: 'High', sublabel: 'Needs attention soon' },
                  { value: 'emergency', label: 'Emergency', sublabel: 'Urgent' },
                ].map((priority) => (
                  <ToggleButton
                    key={priority.value}
                    value={priority.value}
                    sx={{
                      flex: { xs: '1 1 45%', sm: '1 1 auto' },
                      py: 1.5,
                      borderRadius: '8px !important',
                      border: `1px solid ${priorityColors[priority.value].border} !important`,
                      backgroundColor: formData.priority === priority.value ? priorityColors[priority.value].bg : 'white',
                      color: priorityColors[priority.value].text,
                      '&.Mui-selected': {
                        backgroundColor: priorityColors[priority.value].bg,
                        color: priorityColors[priority.value].text,
                        fontWeight: 600,
                      },
                      '&:hover': {
                        backgroundColor: priorityColors[priority.value].bg,
                      },
                      flexDirection: 'column',
                      textTransform: 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{priority.label}</Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>{priority.sublabel}</Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
            {/* Upload Progress */}
            {submitting && uploadProgress > 0 && uploadProgress < 100 && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%' }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: navyBlue,
                        borderRadius: 4
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                    Uploading photo... {uploadProgress}%
                  </Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={submitting}
                sx={{
                  background: `linear-gradient(135deg, ${navyBlue} 0%, #2d5a8a 100%)`,
                  borderRadius: 2,
                  textTransform: 'none',
                  py: 1.5,
                  fontSize: '1rem',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Maintenance Request'}
              </Button>
            </Grid>
          </Grid>
        </form>
        </Box>
      </Paper>
    </Container>
  );
}
